// src/components/transfers/DirectTransferDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Send, Plus, Trash2 } from 'lucide-react';
import { storeApi, productApi } from '../../api';

// 到期状态：过期/临期（7天内）/正常——和其他几处批次相关UI用的是同一套判断逻辑
function getExpiryStatus(expiryDate) {
  if (!expiryDate) return 'none';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiryDate);
  const diffDays = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'expired';
  if (diffDays <= 7) return 'soon';
  return 'ok';
}
function batchOptionLabel(b) {
  const status = getExpiryStatus(b.expiry_date);
  const prefix = status === 'expired' ? '⚠️已过期 · ' : status === 'soon' ? '⏰临期 · ' : '';
  return `${prefix}${b.batch_no}（剩余 ${b.quantity} 个${b.expiry_date ? `，到期 ${b.expiry_date}` : ''}）`;
}

// 总店主动配发——不需要分店先申请，总店自己选分店、选商品、选批次，
// 一步创建并直接发货（对应后端 createDirectTransfer，状态直接是'shipped'，
// 不像分店申请的划拨那样还要走一次单独的"确认发货"）
const DirectTransferDialog = ({ products, onClose, onSubmit }) => {
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [toStoreId, setToStoreId] = useState('');
  const [notes, setNotes] = useState('');

  // 已选的商品行：[{ product_id, product_name, quantity, batch_id, batches, loadingBatches }]
  const [rows, setRows] = useState([]);
  const [pickerProductId, setPickerProductId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadBranches = async () => {
      setLoadingBranches(true);
      try {
        const res = await storeApi.getList();
        if (res.success) {
          setBranches((res.data || []).filter(s => s.type === 'branch' && s.status === 'active'));
        }
      } catch (err) {
        console.error('加载分店失败:', err);
      } finally {
        setLoadingBranches(false);
      }
    };
    loadBranches();
  }, []);

  const addProductRow = async () => {
    if (!pickerProductId) return;
    const product = products.find(p => String(p.id) === String(pickerProductId));
    if (!product) return;
    if (rows.some(r => r.product_id === product.id)) {
      alert('该商品已经在列表里了');
      return;
    }

    const newRow = {
      product_id: product.id,
      product_name: product.name,
      quantity: 1,
      batch_id: '',
      batches: [],
      loadingBatches: true,
    };
    setRows(prev => [...prev, newRow]);
    setPickerProductId('');

    try {
      const res = await productApi.getAvailableBatches(product.id);
      const batches = res.success ? (res.data || []) : [];
      const defaultBatch = batches[0]; // 已按到期日期排好序，默认选最早到期那个
      setRows(prev => prev.map(r =>
        r.product_id === product.id
          ? { ...r, batches, loadingBatches: false, batch_id: defaultBatch ? defaultBatch.id : '' }
          : r
      ));
    } catch (err) {
      setRows(prev => prev.map(r => r.product_id === product.id ? { ...r, loadingBatches: false } : r));
    }
  };

  const updateRow = (product_id, field, value) => {
    setRows(prev => prev.map(r => r.product_id === product_id ? { ...r, [field]: value } : r));
  };

  const removeRow = (product_id) => {
    setRows(prev => prev.filter(r => r.product_id !== product_id));
  };

  const getRowBatch = (row) => row.batches.find(b => String(b.id) === String(row.batch_id));

  const hasInvalidRow = rows.some(row => {
    if (!row.batch_id) return true;
    const batch = getRowBatch(row);
    return !batch || batch.quantity < row.quantity || row.quantity <= 0;
  });

  const handleSubmit = async () => {
    if (!toStoreId) { alert('请选择要配发的分店'); return; }
    if (rows.length === 0) { alert('请至少添加一个商品'); return; }
    if (hasInvalidRow) { alert('有商品未选批次，或数量超过所选批次的剩余库存，请检查'); return; }

    setSubmitting(true);
    try {
      const payload = {
        to_store_id: parseInt(toStoreId),
        items: rows.map(r => ({
          product_id: r.product_id,
          batch_id: parseInt(r.batch_id),
          quantity: parseInt(r.quantity),
        })),
        notes: notes || undefined,
      };
      await onSubmit(payload);
    } catch (err) {
      alert(err.message || '配发失败');
    } finally {
      setSubmitting(false);
    }
  };

  const availableProducts = products.filter(p => !rows.some(r => r.product_id === p.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="border-b p-6 flex justify-between items-center flex-shrink-0">
          <div>
            <h3 className="text-xl font-bold">主动配发</h3>
            <p className="text-sm text-gray-500 mt-0.5">总店直接选分店、选商品和批次，一步配发并发货</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* 选分店 */}
          <div>
            <label className="block text-sm font-medium mb-1">配发到分店 <span className="text-red-500">*</span></label>
            {loadingBranches ? (
              <p className="text-sm text-gray-400">加载分店中...</p>
            ) : (
              <select value={toStoreId} onChange={e => setToStoreId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">请选择分店</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>

          {/* 添加商品 */}
          <div>
            <label className="block text-sm font-medium mb-1">添加商品</label>
            <div className="flex gap-2">
              <select value={pickerProductId} onChange={e => setPickerProductId(e.target.value)}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">选择要配发的商品</option>
                {availableProducts.map(p => (
                  <option key={p.id} value={p.id}>{p.name}（总店库存 {p.stock}）</option>
                ))}
              </select>
              <button onClick={addProductRow} disabled={!pickerProductId}
                className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                <Plus className="w-4 h-4" />添加
              </button>
            </div>
          </div>

          {/* 已选商品列表：各自选数量+批次 */}
          {rows.length > 0 && (
            <div className="space-y-3">
              {rows.map(row => {
                const batch = getRowBatch(row);
                const insufficient = batch && batch.quantity < row.quantity;
                return (
                  <div key={row.product_id} className="border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800 text-sm">{row.product_name}</span>
                      <button onClick={() => removeRow(row.product_id)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">数量</label>
                        <input type="number" min="1" value={row.quantity}
                          onChange={e => updateRow(row.product_id, 'quantity', parseInt(e.target.value) || 1)}
                          className="w-full px-2 py-1.5 border rounded-lg text-sm" />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-gray-500 mb-1">发货批次</label>
                        {row.loadingBatches ? (
                          <p className="text-xs text-gray-400 py-1.5">加载批次中...</p>
                        ) : row.batches.length === 0 ? (
                          <p className="text-xs text-red-500 py-1.5">该商品没有可用批次</p>
                        ) : (
                          <>
                            <select value={row.batch_id}
                              onChange={e => updateRow(row.product_id, 'batch_id', e.target.value)}
                              className="w-full px-2 py-1.5 border rounded-lg text-sm">
                              <option value="">请选择批次</option>
                              {row.batches.map(b => (
                                <option key={b.id} value={b.id}>{batchOptionLabel(b)}</option>
                              ))}
                            </select>
                            {(() => {
                              const selected = row.batches.find(b => String(b.id) === String(row.batch_id));
                              if (!selected) return null;
                              const status = getExpiryStatus(selected.expiry_date);
                              if (status === 'expired') return <p className="text-xs text-red-500 mt-1">⚠️ 所选批次已过期</p>;
                              if (status === 'soon') return <p className="text-xs text-amber-600 mt-1">⏰ 所选批次7天内到期</p>;
                              return null;
                            })()}
                          </>
                        )}
                      </div>
                    </div>
                    {insufficient && (
                      <p className="text-xs text-red-500 mt-1">⚠️ 所选批次库存不足（剩余{batch.quantity}，需要{row.quantity}）</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">备注（可选）</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" rows="2" />
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            取消
          </button>
          <button onClick={handleSubmit} disabled={submitting || rows.length === 0 || hasInvalidRow}
            className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
            <Send className="w-4 h-4" />
            {submitting ? '配发中...' : '确认配发'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DirectTransferDialog;